/**
 * Phase 12 — Audit Log API helpers + UI label/category maps.
 */
import api from "./api";

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

export const listAuditLog = (params = {}) =>
  api.get("/audit-log", { params }).then((r) => r.data);

// ---------------------------------------------------------------------------
// Categories — derived from event_type prefix
// ---------------------------------------------------------------------------

export const CATEGORIES = [
  { value: "", label: "All", prefix: "" },
  { value: "user", label: "Users", prefix: "user" },
  { value: "service_request", label: "Service Requests", prefix: "service_request" },
  { value: "work_order", label: "Work Orders", prefix: "work_order" },
  { value: "diagnostic_report", label: "Diagnostics", prefix: "diagnostic_report" },
  { value: "rma", label: "RMA", prefix: "rma" },
  { value: "payment_request", label: "Payments", prefix: "payment_request" },
  { value: "subscription", label: "Billing", prefix: "subscription" },
  { value: "monitoring", label: "Monitoring", prefix: "monitoring" },
  { value: "system", label: "System", prefix: "system" },
];

// Tints per spec (purple/blue/teal/amber/orange/green/emerald/slate)
export const CATEGORY_TINT = {
  user: "bg-purple-100 text-purple-900 border-purple-300",
  service_request: "bg-blue-100 text-blue-900 border-blue-300",
  work_order: "bg-teal-100 text-teal-900 border-teal-300",
  diagnostic_report: "bg-amber-100 text-amber-900 border-amber-300",
  rma: "bg-orange-100 text-orange-900 border-orange-300",
  payment_request: "bg-green-100 text-green-900 border-green-300",
  subscription: "bg-emerald-100 text-emerald-900 border-emerald-300",
  monitoring: "bg-amber-100 text-amber-900 border-amber-300",
  system: "bg-slate-100 text-slate-700 border-slate-300",
};

export const CATEGORY_LABEL = {
  user: "User",
  service_request: "Service Req",
  work_order: "Work Order",
  diagnostic_report: "Diagnostic",
  rma: "RMA",
  payment_request: "Payment",
  subscription: "Billing",
  monitoring: "Monitoring",
  system: "System",
};

export const eventCategory = (eventType) =>
  (eventType || "").split(".")[0] || "system";

// ---------------------------------------------------------------------------
// Event-type → human label map. New event types fall back to a clean
// titlecase split.
// ---------------------------------------------------------------------------

export const EVENT_LABEL = {
  "user.login": "Login",
  "user.login_failed": "Failed login",
  "user.created": "Created user",
  "user.deactivated": "Deactivated user",
  "user.reactivated": "Reactivated user",
  "user.role_changed": "Role changed",
  "user.password_reset": "Password reset",
  "user.password_changed": "Password changed",
  "user.profile_updated": "Profile updated",
  "service_request.created": "Created service request",
  "service_request.updated": "Updated service request",
  "service_request.converted": "Converted to work order",
  "work_order.created": "Created work order",
  "work_order.status_changed": "Status changed",
  "work_order.assigned": "Tech assigned",
  "diagnostic_report.submitted": "Report submitted",
  "diagnostic_report.finalized": "Report finalized",
  "diagnostic_report.reopened": "Report reopened",
  "rma.created": "RMA created",
  "rma.status_changed": "RMA status changed",
  "rma.voided": "RMA voided",
  "payment_request.created": "Payment request created",
  "payment_request.paid": "Payment received",
  "payment_request.cancelled": "Payment cancelled",
  "subscription.created": "Subscription created",
  "subscription.cancelled": "Subscription cancelled",
  "subscription.billing_status_changed": "Billing status changed",
  "subscription.past_due": "Subscription past due",
  "system.stripe_webhook_received": "Stripe webhook received",
  // Phase 14 — Monitoring
  "monitoring.alert_received": "Alert received",
  "monitoring.alert_acknowledged": "Alert acknowledged",
  "monitoring.alert_resolved": "Alert resolved",
  "monitoring.alert_ignored": "Alert ignored",
  "monitoring.rule_created": "Rule created",
  "monitoring.rule_updated": "Rule updated",
  "monitoring.alert_linked_sr": "Alert linked to service request",
};

export function eventLabel(eventType) {
  if (EVENT_LABEL[eventType]) return EVENT_LABEL[eventType];
  // fallback: human-titlecase the second segment
  const seg = (eventType || "").split(".").pop() || "Event";
  return seg.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Smart-link target → URL where applicable
// ---------------------------------------------------------------------------

export function targetLink(entry) {
  if (!entry || !entry.target_id) return null;
  switch (entry.target_type) {
    case "user":
      return `/team/${entry.target_id}`;
    case "service_request":
      return `/service-requests/${entry.target_id}`;
    case "work_order":
      return `/work-orders/${entry.target_id}`;
    case "rma":
      return `/rma-records/${entry.target_id}`;
    case "diagnostic_report":
      // diagnostic reports are accessed via their parent work order
      if (entry.metadata && entry.metadata.work_order_id) {
        return `/work-orders/${entry.metadata.work_order_id}/diagnostic-report`;
      }
      return null;
    case "subscription":
      return `/billing/${entry.target_id}`;
    case "monitoring_alert":
      return `/monitoring/${entry.target_id}`;
    case "alert_rule":
      return `/monitoring/rules`;
    case "payment_request":
      // No dedicated page — link to the parent service request when known.
      if (entry.metadata && entry.metadata.service_request_id) {
        return `/service-requests/${entry.metadata.service_request_id}`;
      }
      return null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function relativeTime(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const ms = Date.now() - dt.getTime();
  if (ms < 0) return dt.toLocaleString();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

export function isoLocal(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

export function truncate(str, max = 80) {
  if (!str) return "";
  if (str.length <= max) return str;
  return str.slice(0, max).trimEnd() + "…";
}

export const DATE_RANGES = [
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "all", label: "All time" },
];

export function dateRangeToQuery(range) {
  const today = new Date();
  const ymd = (d) => d.toISOString().slice(0, 10);
  if (range === "today") {
    return { date_from: ymd(today), date_to: ymd(today) };
  }
  if (range === "7d") {
    const from = new Date(today);
    from.setDate(today.getDate() - 7);
    return { date_from: ymd(from), date_to: ymd(today) };
  }
  if (range === "30d") {
    const from = new Date(today);
    from.setDate(today.getDate() - 30);
    return { date_from: ymd(from), date_to: ymd(today) };
  }
  return {};
}
